import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Group, Content, Header, Icon, Title, Organization, BoxContent, IconDiv } from './styles';
import { getWorkspaceList } from '../../../services/workspaceService';
import UserPreferenceSingleton from '../../../helpers/userPreferenceSingleton';
import { useHistory } from 'react-router-dom';
import { clearPreviousWorkspaceData } from '../../../services/baseServices';
import { WorkspaceInterface } from '../../../interfaces/WorkspaceInterface';
import Avatar from '../../../component/avatar';
import { appInit } from '../../../services/appService';
import { ROUTES } from '../../../routes/urls';
import { captureException } from '../../../services/logService';
import { setWorkspaceIdInAxios } from '../../../helpers/authTokenHelpers';
import { workspaceSetAnalytics } from '../../../services/analyticsService';
import { updateUserPreference } from '../../../helpers/firebaseHelper';
import { isDev } from '../../../helpers/common';
import WorkSpaceLoading from '../../../component/loading/workSpaceLoading';
import { rootStore } from '../../../mobx/rootStore';
import LayoutWithoutNavbar from '../../../component/layoutWithoutNavbar';
import { observer } from 'mobx-react-lite';
import { toJS } from 'mobx';
import Button from '../../../component/button';
import { AVATAR_BORDER, BUTTON_TYPE } from '../../../global/constants';
import SVGIcon from '../../../assets/images/svg/SVGIcon';

declare global {
  interface Window {
    posthog?: {
      group: (type: string, id: string, properties: Record<string, unknown>) => void;
    };
  }
}

const SelectWorkspace: React.FC = () => {
  // States
  const [loading, setLoading] = useState(false);
  const [selectLoading, setSelectLoading] = useState(false);

  // Mobx state variables
  const { workspaceStore } = rootStore;
  const { workspaceList } = workspaceStore;

  // Other variables
  const history = useHistory();

  // load initial data
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      await getWorkspaceList();
    } catch (e) {
      captureException(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Select workspace
  const selectWorkspace = useCallback(
    async (item: WorkspaceInterface) => {
      try {
        setSelectLoading(true);
        if (!item?.id) return;
        await UserPreferenceSingleton.getInstance().setWorkspace(toJS(item));
        setWorkspaceIdInAxios(item?.id);
        workspaceSetAnalytics(item?.id);
        if (!isDev()) await updateUserPreference({ current_workspace_id: item?.id });
        const clearResult = await clearPreviousWorkspaceData();
        const initResult = await appInit(false, true);
        const intercomPayload = {
          company: {
            name: item?.name,
            id: item?.id
          }
        };
        if (window.posthog) {
          window.posthog.group('Workspace', intercomPayload.company?.id, {
            name: intercomPayload.company?.name
          });
        }
        if (clearResult && initResult) {
          UserPreferenceSingleton.getInstance().setRecentSearch([]);
          history.push(ROUTES.INBOX);
        }
      } catch (e) {
        captureException(e);
      } finally {
        setSelectLoading(false);
      }
    },
    [history]
  );

  // Redirect on selected workspace
  const redirectSelectedWorkspace = useCallback(() => {
    history.push(ROUTES.WORKSPACE);
  }, [history]);

  // List of organization
  const organizationList = useMemo(() => {
    if (!workspaceList?.length) return null;
    return workspaceList?.map((item) => (
      <BoxContent key={item?.id} onClick={() => selectWorkspace(item)} disabled={selectLoading}>
        {item?.logoUrl ? (
          <Avatar
            imgSrc={item.logoUrl}
            name={item?.name}
            size={38}
            borderVariant={AVATAR_BORDER.NONE}
            borderRadius='var(--border-radius-6)'
          />
        ) : (
          <IconDiv>
            <SVGIcon name='workspace-icon' width={22} height={22} viewBox='0 0 36 36' />
          </IconDiv>
        )}
        <div className='name-text'>{item?.name}</div>
      </BoxContent>
    ));
  }, [workspaceList, selectLoading, selectWorkspace]);

  return (
    <>
      <LayoutWithoutNavbar>
        <Group isLoading={loading || selectLoading}>
          {((loading && workspaceList?.length === 0) || selectLoading) && <WorkSpaceLoading />}
          {workspaceList && workspaceList?.length > 0 && !selectLoading && (
            <Content>
              <Header>
                <Icon>
                  <SVGIcon
                    name='workspace-icon'
                    width={36}
                    height={36}
                    viewBox='0 0 36 36'
                    fill='var(--text-primary)'
                  />
                </Icon>
                <Title> Select Organisation</Title>
              </Header>
              <Organization>{organizationList}</Organization>
              <div className='button'>
                <Button
                  title='Add New Workspace'
                  variant={BUTTON_TYPE.SECONDARY}
                  onClick={redirectSelectedWorkspace}
                  iconPosition='start'
                  iconName='plus-icon'
                />
              </div>
            </Content>
          )}
        </Group>
      </LayoutWithoutNavbar>
    </>
  );
};

export default observer(SelectWorkspace);
